import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'
import { SchmancyElement } from '@schmancy/mixins/index'
import { layoutViewportPlatform } from '@schmancy/overlay/overlay.soft-keyboard'
import { html } from 'lit'
import { customElement, property, query, queryAssignedElements } from 'lit/decorators.js'
import { filter, fromEvent, merge, takeUntil } from 'rxjs'

@customElement('schmancy-dropdown')
export class SchmancyDropdown extends SchmancyElement {
	@property({ type: Boolean, reflect: true })
	open = false

	@property({ type: String })
	placement:
		| 'top'
		| 'top-start'
		| 'top-end'
		| 'right'
		| 'right-start'
		| 'right-end'
		| 'bottom'
		| 'bottom-start'
		| 'bottom-end'
		| 'left'
		| 'left-start'
		| 'left-end' = 'bottom-start'

	@property({ type: Number })
	distance = 8

	@query('.trigger-container') triggerContainer!: HTMLElement
	@query('.dropdown-content-container') contentContainer!: HTMLElement
	@queryAssignedElements({ flatten: true }) contentElements!: HTMLElement[]

	@queryAssignedElements({ slot: 'trigger', flatten: true })
	triggerElements!: Array<HTMLElement>

	private cleanupPositioner?: () => void

	connectedCallback() {
		super.connectedCallback()

		merge(
			fromEvent<MouseEvent>(document, 'click').pipe(filter(event => !event.composedPath().includes(this))),
			fromEvent<KeyboardEvent>(document, 'keydown').pipe(filter(event => event.key === 'Escape')),
		)
			.pipe(
				filter(() => this.open),
				takeUntil(this.disconnecting),
			)
			.subscribe(() => {
				this.open = false
			})
	}

	disconnectedCallback() {
		this.cleanupPositioner?.()
		this.cleanupPositioner = undefined
		super.disconnectedCallback()
	}

	toggle() {
		this.open = !this.open
	}

	updated(changedProps: Map<string, unknown>) {
		super.updated(changedProps)
		if (!changedProps.has('open')) return

		this.cleanupPositioner?.()
		this.cleanupPositioner = undefined

		// The content stays where the consumer rendered it and is lifted into the
		// top layer instead of cloned elsewhere: a copy under <body> loses the
		// theme host's tokens, the shadow root's utility sheet, and every listener.
		if (!this.open) {
			if (this.contentContainer.matches(':popover-open')) this.contentContainer.hidePopover()
			return
		}
		if (!this.isConnected) return
		if (!this.contentContainer.matches(':popover-open')) this.contentContainer.showPopover()

		this.cleanupPositioner = autoUpdate(this.triggerContainer, this.contentContainer, () => {
			computePosition(this.triggerContainer, this.contentContainer, {
				placement: this.placement,
				strategy: 'fixed',
				middleware: [
					offset(this.distance),
					flip({
						fallbackPlacements: ['top-start', 'bottom-start'],
					}),
					shift({ padding: 0 }),
				],
				platform: layoutViewportPlatform,
			}).then(({ x, y }) => {
				Object.assign(this.contentContainer.style, {
					left: `${x}px`,
					top: `${y - 8}px`,
				})
			})
		})
	}

	private handleTriggerClick(e: Event) {
		e.stopPropagation()
		this.toggle()
	}

	render() {
		return html`
			<div class="trigger-container" @click=${this.handleTriggerClick}>
				<slot name="trigger"></slot>
			</div>

			<div
				class="dropdown-content-container fixed inset-auto m-0 p-0 border-0 bg-transparent overflow-visible text-inherit"
				popover="manual"
			>
				<slot></slot>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-dropdown': SchmancyDropdown
	}
}
